/**
 * Public API Client for authentication and bookmarks endpoints
 * Handles token storage and automatic authorization headers
 */

const PUBLIC_AUTH_TOKEN_KEY = 'public_auth_token';

// Bookmark Categories
export const BOOKMARK_CATEGORIES = {
  NEWS: 'செய்தி',
  ARTICLES: 'கட்டுரைகள்',
  PHOTOS: 'புகைப்படங்கள்',
  VIDEOS: 'வீடியோக்கள்',
} as const;

export const CATEGORY_LABELS: Record<string, string> = {
  'செய்தி': 'News',
  'கட்டுரைகள்': 'Articles',
  'புகைப்படங்கள்': 'Photos',
  'வீடியோக்கள்': 'Videos',
};

export type BookmarkCategory = typeof BOOKMARK_CATEGORIES[keyof typeof BOOKMARK_CATEGORIES];

export interface Bookmark {
  id: string;
  user_id: string;
  slug: string;
  category: BookmarkCategory;
  title?: string;
  thumbnail_url?: string;
  description?: string;
  article_url?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface CategoryInfo {
  key: string;
  value: string;
  label: string;
  labelTamil: string;
}

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

class PublicApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || '/api/public';
  }

  private getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(PUBLIC_AUTH_TOKEN_KEY);
  }

  setToken(token: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(PUBLIC_AUTH_TOKEN_KEY, token);
  }

  removeToken(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(PUBLIC_AUTH_TOKEN_KEY);
  }

  hasToken(): boolean {
    return !!this.getToken();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(data.message || 'Request failed', response.status, data);
    }

    return data;
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Singleton instance
const publicApiClient = new PublicApiClient();
export default publicApiClient;

// Reaction Types
export const REACTION_TYPES = {
  LIKE: 'like',
  DISLIKE: 'dislike',
} as const;

export type ReactionType = typeof REACTION_TYPES[keyof typeof REACTION_TYPES];

export interface Reaction {
  id: string;
  user_id: string;
  slug: string;
  reaction_type: ReactionType;
  category?: string;
  title?: string;
  thumbnail_url?: string;
  description?: string;
  article_url?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Reactions API Helper
 */
export const reactionsApi = {
  /**
   * Get reaction types (no auth required)
   */
  async getReactionTypes(): Promise<{
    success: boolean;
    types: { key: string; value: string; label: string }[];
    validValues: string[];
  }> {
    return publicApiClient.get('/reactions/types');
  },

  /**
   * Get reaction counts for a story (no auth required)
   */
  async getReactionCounts(slug: string): Promise<{
    success: boolean;
    slug: string;
    likes: number;
    dislikes: number;
  }> {
    return publicApiClient.get(`/reactions/counts/${encodeURIComponent(slug)}`);
  },

  /**
   * Get reaction counts for multiple stories (no auth required)
   */
  async getMultipleReactionCounts(slugs: string[]): Promise<{
    success: boolean;
    counts: Record<string, { likes: number; dislikes: number }>;
  }> {
    return publicApiClient.post('/reactions/counts', { slugs });
  },

  /**
   * Check user's reactions on multiple stories
   */
  async checkReactions(slugs: string[]): Promise<{
    success: boolean;
    reactions: Record<string, ReactionType | null>;
  }> {
    return publicApiClient.post('/reactions/check', { slugs });
  },

  /**
   * Toggle like on a story
   */
  async toggleLike(data: {
    slug: string;
    category?: string;
    title?: string;
    thumbnail_url?: string;
    description?: string;
    article_url?: string;
  }): Promise<{
    success: boolean;
    message: string;
    liked: boolean;
    reaction?: Reaction;
  }> {
    return publicApiClient.post('/reactions/like', data);
  },

  /**
   * Toggle dislike on a story
   */
  async toggleDislike(data: {
    slug: string;
    category?: string;
    title?: string;
    thumbnail_url?: string;
    description?: string;
    article_url?: string;
  }): Promise<{
    success: boolean;
    message: string;
    disliked: boolean;
    reaction?: Reaction;
  }> {
    return publicApiClient.post('/reactions/dislike', data);
  },
};

/**
 * Bookmarks API Helper
 */
export const bookmarksApi = {
  /**
   * Get available categories (no auth required)
   */
  async getAvailableCategories(): Promise<{
    success: boolean;
    categories: CategoryInfo[];
    validValues: string[];
  }> {
    return publicApiClient.get('/bookmarks/available-categories');
  },

  /**
   * Get all bookmarks with optional filtering
   */
  async getBookmarks(options?: {
    category?: BookmarkCategory;
    page?: number;
    limit?: number;
    sortBy?: 'created_at' | 'title';
    sortOrder?: 'asc' | 'desc';
  }): Promise<{
    success: boolean;
    bookmarks: Bookmark[];
    pagination: PaginationInfo;
  }> {
    const params = new URLSearchParams();
    if (options?.category) params.append('category', options.category);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);

    const query = params.toString();
    return publicApiClient.get(`/bookmarks${query ? `?${query}` : ''}`);
  },

  /**
   * Get bookmark counts by category
   */
  async getCategories(): Promise<{
    success: boolean;
    categories: { category: string; count: number }[];
  }> {
    return publicApiClient.get('/bookmarks/categories');
  },

  /**
   * Get bookmark count
   */
  async getCount(category?: BookmarkCategory): Promise<{
    success: boolean;
    count: number;
  }> {
    const query = category ? `?category=${encodeURIComponent(category)}` : '';
    return publicApiClient.get(`/bookmarks/count${query}`);
  },

  /**
   * Check if an article is bookmarked
   */
  async isBookmarked(slug: string): Promise<{
    success: boolean;
    slug: string;
    bookmarked: boolean;
  }> {
    return publicApiClient.get(`/bookmarks/${encodeURIComponent(slug)}`);
  },

  /**
   * Check multiple articles
   */
  async checkBookmarks(slugs: string[]): Promise<{
    success: boolean;
    bookmarks: Record<string, boolean>;
  }> {
    return publicApiClient.post('/bookmarks/check', { slugs });
  },

  /**
   * Add a bookmark
   */
  async addBookmark(data: {
    slug: string;
    category?: BookmarkCategory;
    title?: string;
    thumbnail_url?: string;
    description?: string;
    article_url?: string;
    metadata?: Record<string, unknown>;
  }): Promise<{
    success: boolean;
    message: string;
    bookmark: Bookmark;
  }> {
    return publicApiClient.post('/bookmarks', data);
  },

  /**
   * Toggle bookmark (add if not exists, remove if exists)
   */
  async toggleBookmark(data: {
    slug: string;
    category?: BookmarkCategory;
    title?: string;
    thumbnail_url?: string;
    description?: string;
    article_url?: string;
    metadata?: Record<string, unknown>;
  }): Promise<{
    success: boolean;
    message: string;
    bookmarked: boolean;
    category: string | null;
    bookmark?: Bookmark;
  }> {
    return publicApiClient.post('/bookmarks/toggle', data);
  },

  /**
   * Remove a bookmark
   */
  async removeBookmark(slug: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return publicApiClient.delete(`/bookmarks/${encodeURIComponent(slug)}`);
  },

  /**
   * Clear all bookmarks (optionally filtered by category)
   */
  async clearBookmarks(category?: BookmarkCategory): Promise<{
    success: boolean;
    message: string;
    count: number;
  }> {
    const query = category ? `?category=${encodeURIComponent(category)}` : '';
    return publicApiClient.delete(`/bookmarks${query}`);
  },
};
